import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { RxCross2 } from "react-icons/rx";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import { setViewImage, updateViewState } from "../store/Slices/ViewManagerSlice";
import ReactSlickCarousel from "./ReactSlickCarousel";
import LightboxVideoPlayer from "./LightboxVideoPlayer";
import { isHlsUrl } from "../utils/mediaPlayUrl";
import { useLightboxZoom } from "../hooks/useLightboxZoom";

function pathForExt(url: string) {
  return url.split(/[?#]/)[0] || url;
}

function isVideoUrl(url: string) {
  return (
    isHlsUrl(url) ||
    /\.(mp4|webm|ogg|mov|m4v|3gp|mkv|avi)$/i.test(pathForExt(url))
  );
}

const CLOSE_CLEAR_MS = 320;

/** Single media: zoom + fade in/out. Multi (albums later): carousel. */
const ImageViewer: React.FC = () => {
  const viewImageSlice = useAppSelector((state) => state.ViewManager);
  const dispatch = useAppDispatch();
  const open = !!viewImageSlice.show_image;

  const liveSlides = Array.isArray(viewImageSlice.image_src)
    ? viewImageSlice.image_src.filter(
        (src): src is string => !!src && typeof src === "string",
      )
    : [];

  /** Keep last media during leave so zoom+fade out can play. */
  const [slides, setSlides] = useState<string[]>(liveSlides);
  const clearTimer = useRef<number | null>(null);

  const liveKey = liveSlides.join("\0");
  useEffect(() => {
    if (open && liveSlides.length > 0) {
      if (clearTimer.current != null) {
        window.clearTimeout(clearTimer.current);
        clearTimer.current = null;
      }
      setSlides(liveSlides);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- liveKey tracks content
  }, [open, liveKey]);

  const isMulti = slides.length >= 2;
  const src = slides[0] ?? "";
  const { stageRef, scale, resetZoom } = useLightboxZoom(open && !isMulti);

  useEffect(() => {
    if (open) resetZoom();
  }, [open, src, resetZoom]);

  const handleCloseModal = () => {
    resetZoom();
    dispatch(updateViewState({ show_image: false }));
    if (clearTimer.current != null) window.clearTimeout(clearTimer.current);
    clearTimer.current = window.setTimeout(() => {
      dispatch(setViewImage({ show_image: false, image_src: [] }));
      setSlides([]);
      clearTimer.current = null;
    }, CLOSE_CLEAR_MS);
  };

  /** Close when clicking empty space; keep open on media / controls / arrows. */
  const handleOverlayClick = (e: React.MouseEvent) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (
      t.closest(
        [
          ".media-lightbox-item",
          ".lightbox-video-shell",
          ".lightbox-video-controls",
          ".lightbox-carousel-player",
          ".custom-prev-arrow",
          ".custom-next-arrow",
          "video",
          "[data-lightbox-keep-open]",
        ].join(","),
      )
    ) {
      return;
    }
    handleCloseModal();
  };

  const mediaTransform = {
    transform: `scale(${scale})`,
    transformOrigin: "center center",
    transition: scale === 1 ? "transform 160ms ease-out" : "none",
  } as const;

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      transition
      className="relative z-50"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/80 backdrop-blur-[2px] transition duration-300 ease-out data-[closed]:opacity-0"
      />

      <div
        className="fixed inset-0 z-50 flex flex-col"
        onClick={handleOverlayClick}
      >
        <button
          type="button"
          onClick={handleCloseModal}
          className="absolute right-3 top-3 z-[80] rounded-full bg-black/40 p-2 text-white transition hover:bg-black/60"
          aria-label="Закрыть"
        >
          <RxCross2 className="h-6 w-6" />
        </button>

        <div
          className={`flex min-h-0 flex-1 items-center justify-center pb-6 pt-14 ${
            isMulti
              ? "overflow-visible px-0"
              : "overflow-hidden px-3"
          }`}
        >
          <DialogPanel
            transition
            as="div"
            className={`relative bg-transparent shadow-none outline-none transition duration-300 ease-out data-[closed]:scale-90 data-[closed]:opacity-0 ${
              isMulti
                ? "flex h-[min(85vh,900px)] w-screen max-w-none items-center justify-center overflow-visible"
                : "w-auto max-w-[min(100vw-1.5rem,1600px)]"
            }`}
          >
            <DialogTitle className="sr-only">Просмотр медиа</DialogTitle>

            {slides.length > 0 &&
              (isMulti ? (
                <ReactSlickCarousel />
              ) : isVideoUrl(src) ? (
                <LightboxVideoPlayer
                  src={src}
                  stageRef={stageRef}
                  mediaStyle={mediaTransform}
                  className="media-lightbox-item max-h-[min(88vh,1080px)] max-w-[min(96vw,1400px)] w-auto rounded-lg bg-black object-contain shadow-lg"
                />
              ) : (
                <div
                  ref={stageRef}
                  className="flex max-h-[min(92vh,1100px)] max-w-[min(96vw,1400px)] items-center justify-center overflow-visible"
                >
                  <img
                    className="media-lightbox-item max-h-[min(92vh,1100px)] max-w-[min(96vw,1400px)] w-auto rounded-lg object-contain shadow-lg"
                    style={mediaTransform}
                    src={src}
                    alt="Media"
                    draggable={false}
                  />
                </div>
              ))}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default ImageViewer;
